import React from 'react';
import { Package } from 'lucide-react';
import { getProductImage } from '../../utils/imageUtils';

/**
 * ProductCatalogueCard - Image-focused product card for the catalogue
 * 
 * @param {Object} props
 * @param {Object} props.product - Product data
 * @param {Function} props.onClick - Callback when card is clicked
 * @param {string} props.viewMode - 'compact' or 'large'
 */
const ProductCatalogueCard = ({ product, onClick, viewMode = 'compact' }) => {
  const mainImage = getProductImage(product.images);

  // Compact View - Smaller cards
  if (viewMode === 'compact') {
    return (
      <div
        onClick={onClick}
        className="group bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
      >
        {/* Product Image */}
        <div className="relative aspect-square bg-gray-100 overflow-hidden">
          {mainImage ? (
            <img
              src={mainImage}
              alt={product.brand_name || 'Product'}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <Package className="w-12 h-12 text-gray-300" />
            </div>
          )}
          
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
            <span className="text-white font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/50 px-3 py-1.5 rounded-full text-xs">
              View Gallery
            </span>
          </div>
        </div>

        {/* Product Info - Compact */}
        <div className="p-3 space-y-1.5">
          {/* Product Code Badge */}
          <span className="inline-block px-2 py-0.5 text-[10px] font-bold rounded-full bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black">
            {product.product_code}
          </span>

          {/* Brand Name */}
          <h3 className="text-xs font-semibold text-gray-800 line-clamp-1">
            {product.brand_name || 'Unknown Brand'}
          </h3>

          {/* Size & Type */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-800">
              {product.size_dimension || 'N/A'}
            </span>
            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-800">
              {product.tile_type_name || 'N/A'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Large View - Bigger cards with more info
  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
    >
      {/* Product Image - Larger */}
      <div className="relative aspect-square bg-gray-100 overflow-hidden">
        {mainImage ? (
          <img
            src={mainImage}
            alt={product.brand_name || 'Product'}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <Package className="w-20 h-20 text-gray-300" />
          </div>
        )}
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
          <span className="text-white font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/50 px-4 py-2 rounded-full text-sm">
            View Gallery
          </span>
        </div>
      </div>

      {/* Product Info - Large */}
      <div className="p-4 space-y-2">
        {/* Product Code Badge - Full width on its own line */}
        <div>
          <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black shadow-sm">
            {product.product_code}
          </span>
        </div>

        {/* Brand, Size, Type - Evenly spaced on one line */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-gray-900 truncate flex-1">
            {product.brand_name || 'Unknown'}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800">
            {product.size_dimension || 'N/A'}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800">
            {product.tile_type_name || 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductCatalogueCard;
